import { datadogLogs, StatusType } from '@datadog/browser-logs';
import * as Sentry from '@sentry/react';
import { getRandomSessionId } from '../utils/core';

interface DashboardContext {
  userId: number;
  companyId: number;
}

interface PartnerContext {
  userId: string;
  partnerId: number;
}

type LoggerContext = DashboardContext | PartnerContext;

const EXTERNAL_BROWSER_REJECTION = /^Object Not Found Matching Id:\d+, MethodName:[^,]+, ParamCount:\d+$/;

function isExternalBrowserRejection(event: { message?: string; error?: { message?: string } }): boolean {
  return [event.message, event.error?.message].some(message => message !== undefined && EXTERNAL_BROWSER_REJECTION.test(message));
}

export const sessionId = getRandomSessionId();

export class Logger {

  private logger = datadogLogs.logger;

  constructor() {
    if (process.env.NODE_ENV === 'production') {
      Sentry.init({
        dsn: process.env.SENTRY_DSN,
        release: process.env.VERSION,
        enableLogs: true,
        environment: process.env.APP_ENV,
        ignoreErrors: [EXTERNAL_BROWSER_REJECTION]
      });
    }

    if (!process.env.DATADOG_CLIENT_TOKEN || !process.env.APP_NAME || !process.env.VERSION) {
      throw new Error('Missing required environment variables for Logger');
    }

    datadogLogs.init({
      clientToken: process.env.DATADOG_CLIENT_TOKEN,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: process.env.APP_NAME,
      site: 'datadoghq.eu',
      version: process.env.VERSION,
      beforeSend: event => !isExternalBrowserRejection(event)
    });

    datadogLogs.logger.setContext({ version: process.env.VERSION });
    datadogLogs.logger.setLevel(StatusType.info);
    datadogLogs.logger.setHandler(process.env.APP_ENV === 'production' ? 'http' : 'console');
  }

  public setContext(context: LoggerContext) {
    this.logger.setContext({ version: process.env.VERSION, sessionId, ...context });
    Sentry.setUser({ id: context.userId });
  }

  private log(message: string, level: StatusType, data: object) {
    this.logger.log(message, { ...data, level }, level);
    Sentry.addBreadcrumb({ message, data });
  }

  public info(message: string, data: Record<string, unknown> = {}) {
    this.log(message, StatusType.info, data);
    Sentry.logger.info(message, { ...data, sessionId });
  }

  public warn(message: string, data: Record<string, unknown> = {}) {
    this.log(message, StatusType.warn, data);
    Sentry.logger.warn(message, { ...data, sessionId });
  }

  public error(message: string, data: Record<string, unknown> = {}) {
    this.log(message, StatusType.error, data);
    Sentry.logger.error(message, { ...data, sessionId });
  }

  public report(message: string, data: Record<string, unknown> = {}) {
    Sentry.captureException(message, data);
  }

}

export const logger = new Logger();